import { useNavigate } from 'react-router-dom';

import { useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { useToaster } from '@components/toaster';
import { CareerSiteLanguageType } from '@locale/locale-store';
import { useScopedTranslate } from '@locale/translate';

import {
  activeFollowers,
  ApplicationformData,
  bulkGrantAccessToApplicants,
  bulkRemoveApplicationAccess,
  create,
  deleteLanguage,
  duplicate,
  edit,
  editRewards,
  editSocial,
  EmailTemplateData,
  exportApplicants,
  ExportApplicantsData,
  FollowerResponse,
  followers,
  getApplicationForm,
  getEmailTemplate,
  getJobOrder,
  getRewards,
  getSocial,
  loadApplicationAccessForJob,
  loadFromDbUnhandled,
  moveToArchive,
  moveToDrafts,
  patchApplicationForm,
  pinJob,
  publish,
  SocialData,
  SubmitError,
  unpinJob,
  updateEmailTemplate,
  unpublish,
} from 'api/jobs/requests';
import { useStore } from 'models/Provider';
import type { CreateJobType } from 'types/jobs';

export const useGetJob = (id: number, enabled?: boolean) => {
  const { t } = useScopedTranslate(JobHooksLocale);
  const { showError } = useToaster();
  return useQuery({
    queryKey: ['job', id],
    queryFn: () => loadFromDbUnhandled(id),
    enabled,
    onError: () => {
      showError(t('getJobError'));
    },
  });
};

export const useCreateJob = () => {
  const { showSuccess, showError } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const navigate = useNavigate();
  return useMutation((data: CreateJobType) => create(data), {
    onSuccess: (response) => {
      showSuccess(t('jobCreated'));
      navigate(`/job/${response.data.id}/edit/form`, { state: 'override' });
    },
    onError: () => {
      showError(t('createJobError'));
    },
  });
};

export const useUpdateJob = () => {
  const { showSuccess, showError } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  return useMutation((data: { jobId: number; job: CreateJobType }) => edit(data.jobId, data.job), {
    onSuccess: async (_, variables) => {
      await queryClient.refetchQueries(['job', variables.jobId]);
      await queryClient.refetchQueries(['job-application-form', variables.jobId]);
      showSuccess(t('jobUpdated'));
    },
    onError: () => {
      showError(t('updateJobError'));
    },
  });
};

export const useDuplicateJob = () => {
  return useMutation(
    (props: { jobId: number; onSuccess: () => void; onError: () => void }) =>
      duplicate(props.jobId),
    {
      onSuccess: (_, { onSuccess }) => {
        onSuccess?.();
      },
      onError: (_, { onError }) => {
        onError?.();
      },
    }
  );
};

export const useGetApplicationForm = (id: number, enabled?: boolean) => {
  const { showError } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  return useQuery({
    queryKey: ['job-application-form', id],
    queryFn: () => getApplicationForm(id),
    enabled,
    onError: () => {
      showError(t('getApplicationFormError'));
    },
  });
};

export const usePatchApplicationForm = () => {
  const { showSuccess, showError } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  return useMutation(
    (jobdata: { id: number; data: ApplicationformData }) =>
      patchApplicationForm(jobdata.id, jobdata.data),
    {
      mutationKey: ['patch-application-form'],
      onSuccess: async (_, variables) => {
        showSuccess(t('applicationFormUpdated'));
        await queryClient.refetchQueries(['job', variables.id]);
        await queryClient.refetchQueries(['job-application-form', variables.id]);
      },
      onError: () => {
        showError(t('updateApplicationFormError'));
      },
    }
  );
};

export const useGetActiveFollowers = (id: number, onSuccess?: (data: FollowerResponse) => void) => {
  return useQuery({
    queryKey: ['job', 'followers', id],
    queryFn: () => activeFollowers(id),
    onSuccess,
  });
};

export const useUpdateFollowers = () => {
  const { showSuccess, showError } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  return useMutation(
    (data: { jobId: number; followerIds: number[]; appFollowerIds: number[] }) =>
      followers(data.jobId, { followers: data.followerIds, appFollowers: data.appFollowerIds }),
    {
      mutationKey: ['update-followers'],
      onSuccess: (_, variables) => {
        queryClient.refetchQueries(['job', 'followers', variables.jobId]);
        queryClient.refetchQueries(['job', variables.jobId]);
        showSuccess(t('accessUpdated'));
      },
      onError: () => {
        showError(t('accessUpdateError'));
      },
    }
  );
};

export const useGetSocial = (jobId: number, onSuccess?: (data: SocialData) => void) => {
  return useQuery({
    queryKey: ['job', 'social', jobId],
    queryFn: () => getSocial(jobId),
    onSuccess,
  });
};

export const useEditSocial = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { jobId: number; image: any; title: string; description: string }) => {
      const formData = new FormData();
      formData.set('title', data.title);
      data.image && formData.set('image', data.image);
      formData.set('description', data.description);
      return editSocial(data.jobId, formData);
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries(['job', 'social', variables.jobId]);
        queryClient.refetchQueries(['job', 'rewards', variables.jobId]);
      },
    }
  );
};

export const useGetRewards = (jobId: number, onSuccess?: () => void) => {
  return useQuery({
    queryKey: ['job', 'rewards', jobId],
    queryFn: () => getRewards(jobId),
    onSuccess,
  });
};

export const useEditRewards = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { jobId: number; hasRewards: boolean; rewardsTitle?: string; rewards?: string }) =>
      editRewards(data.jobId, {
        hasRewards: data.hasRewards,
        rewardsTitle: data.rewardsTitle,
        rewards: data.rewards,
      }),
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries(['job', 'rewards', variables.jobId]);
      },
    }
  );
};

export const useGetEmailTemplate = (
  jobId: number,
  onSuccess?: (data: EmailTemplateData) => void
) => {
  return useQuery({
    queryKey: ['job', 'email-template', jobId],
    queryFn: () => getEmailTemplate(jobId),
    onSuccess,
  });
};

export const useUpdateEmailTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { jobId: number; useCompanyEmailTemplate: boolean; contentHtml: string }) =>
      updateEmailTemplate(data.jobId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries(['job', 'email-template', variables.jobId]);
      },
    }
  );
};

export const useDeleteLanguage = () => {
  const { showError, showSuccess } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  return useMutation(
    (data: { jobId: number; langKey: CareerSiteLanguageType }) =>
      deleteLanguage(data.jobId, data.langKey),
    {
      mutationKey: ['delete-language'],
      onSuccess: (response, variables) => {
        if (response.error && response.status === 400) {
          if (response.data === 'This language currently has applicants and cannot be deleted') {
            showError(t('deleteLanguageApplicantError'));
          }
        } else {
          showSuccess(t('languageDeleted'));
        }
        queryClient.refetchQueries(['job-application-form', variables.jobId]);
        queryClient.refetchQueries(['job', variables.jobId]);
      },
      onError: () => {
        showError(t('deleteLanguageError'));
      },
    }
  );
};

export const usePublishJob = () => {
  const { t } = useScopedTranslate(JobHooksLocale);
  const { showError, showSuccess } = useToaster();
  return useMutation((jobId: number) => publish(jobId), {
    mutationKey: ['publish-job'],
    onSuccess: () => {
      showSuccess(t('jobPublished'));
    },
    onError: () => {
      showError(t('jobPublishedError'));
    },
  });
};

export const useUnpublishJob = () => {
  const { showError, showSuccess } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  return useMutation((jobId: number) => unpublish(jobId), {
    mutationKey: ['un-publish-job'],
    onSuccess: (_, jobId) => {
      queryClient.refetchQueries(['job', jobId]);
      showSuccess(t('jobUnpublished'));
    },
    onError: () => {
      showError(t('jobUnpublishedError'));
    },
  });
};

export const useExportApplicants = () => {
  const { showError } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  return useMutation((data: ExportApplicantsData) => exportApplicants(data), {
    mutationKey: ['export-applicants'],
    onError: () => {
      showError(t('exportError'));
    },
  });
};

export const useArchiveJob = () => {
  const { showError, showSuccess } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  return useMutation((jobId: number) => moveToArchive(jobId), {
    mutationKey: ['archive-job'],
    onSuccess: (_, jobId) => {
      queryClient.refetchQueries(['job', jobId]);
      showSuccess(t('archivedSuccess'));
    },
    onError: () => {
      showError(t('archivedError'));
    },
  });
};

export const useMoveToDrafts = () => {
  const { showError, showSuccess } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  return useMutation((jobId: number) => moveToDrafts(jobId), {
    mutationKey: ['draft-job'],
    onSuccess: (_, jobId) => {
      queryClient.refetchQueries(['job', jobId]);
      showSuccess(t('movedToDraftSuccess'));
    },
    onError: () => {
      showError(t('movedToDraftError'));
    },
  });
};

export const useJobOrderingList = () => {
  const { showError } = useToaster();
  const { t } = useStore();
  return useQuery({
    queryKey: ['job-order-data'],
    queryFn: getJobOrder,
    onError: () => {
      showError(t('company.settings.joborder.toast.get.error'));
    },
  });
};

export const usePinJob = () => {
  const { current, t } = useStore();
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation(
    (data: { jobId: number; ordering: number }) => pinJob(data.jobId, data.ordering),
    {
      mutationKey: ['pin-job'],
      onSuccess: () => {
        current.info.isEnterprise
          ? queryClient.invalidateQueries(['department-order-data'])
          : queryClient.invalidateQueries(['job-order-data']);
        showSuccess(t('company.settings.joborder.toast.job'));
      },
      onError: (error: SubmitError) => {
        showError(
          error.response ? error.response.data.error ?? error.response.data.ordering : error.message
        );
      },
    }
  );
};

export const useUnPinJob = () => {
  const { current, t } = useStore();
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation((jobId: number) => unpinJob(jobId), {
    mutationKey: ['unpin-job'],
    onSuccess: () => {
      current.info.isEnterprise
        ? queryClient.invalidateQueries(['department-order-data'])
        : queryClient.invalidateQueries(['job-order-data']);
      showSuccess(t('company.settings.joborder.toast.job'));
    },
    onError: (error: SubmitError) => {
      showError(error.response ? error.response.data.error : error.message);
    },
  });
};

export const useUserApplicationAccessForJob = (jobId: number, userId: number) => {
  return useQuery({
    queryKey: ['user-application-access-job', jobId, userId],
    queryFn: () => loadApplicationAccessForJob(jobId, userId),
  });
};

export const useBulkGrantAccessToApplicants = (
  options?: UseMutationOptions<
    AxiosResponse,
    unknown,
    { jobId: number; applications: string[]; users: number[] }
  >
) => {
  const queryClient = useQueryClient();
  return useMutation(bulkGrantAccessToApplicants, {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.refetchQueries(['user-application-access-job', variables.jobId]);
      queryClient.refetchQueries(['job', 'followers', variables.jobId]);
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useBulkRemoveApplicationAccess = (
  options?: UseMutationOptions<
    AxiosResponse,
    unknown,
    { jobId: number; applications: string[]; users: number[] }
  >
) => {
  const queryClient = useQueryClient();
  return useMutation(bulkRemoveApplicationAccess, {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.refetchQueries(['user-application-access-job', variables.jobId]);
      queryClient.refetchQueries(['job', 'followers', variables.jobId]);
      options?.onSuccess?.(data, variables, context);
    },
  });
};

const JobHooksLocale = {
  en: {
    jobCreated: 'Job created as draft',
    jobUpdated: 'Job updated successfully',
    deleteLanguageApplicantError: 'This language currently has applicants and cannot be deleted',
    languageDeleted: 'Language deleted successfully',
    applicationFormUpdated: 'Application form updated successfully',
    accessUpdated: 'Access for job updated successfully',
    archivedSuccess: 'Job archived successfully',
    archivedError: 'Job could not be archived',
    jobUnpublished: 'Job unpublished successfully',
    jobUnpublishedError: 'Job could not be unpublished',
    jobPublished: 'Job published successfully',
    jobPublishedError: 'Job could not be published',
    movedToDraftSuccess: 'Job moved to drafts successfully',
    movedToDraftError: 'Job could not be moved to drafts',
    getApplicationFormError: 'Could not load job application form',
    updateApplicationFormError: 'Could not update job application form',
    createJobError: 'Could not create job',
    updateJobError: 'Could not update job',
    accessUpdateError: 'Could not update job access',
    deleteLanguageError: 'Could not delete language',
    exportError: 'Could not export applicants',
    getJobError: 'Could not get job',
  },
  is: {
    jobCreated: 'Starf búið til sem drög',
    jobUpdated: 'Starf hefur verið uppfært',
    deleteLanguageApplicantError: 'Þetta tungumál hefur umsækjendur og getur ekki verið eytt',
    languageDeleted: 'Tungumál fjarlægt',
    applicationFormUpdated: 'Umsóknarform hefur verið uppfært',
    accessUpdated: 'Aðgangur starfs hefur verið uppfærður',
    archivedSuccess: 'Starf fært í geymslu',
    archivedError: 'Ekki tókst að færa starf í geymslu',
    jobUnpublished: 'Starf fært í drög',
    jobUnpublishedError: 'Ekki tókst að færa starf í drög',
    jobPublished: 'Starf birt',
    jobPublishedError: 'Ekki tókst að birta starf',
    movedToDraftSuccess: 'Starf fært í drög',
    movedToDraftError: 'Ekki tókst að færa starf í drög',
    getApplicationFormError: 'Ekki tókst að sækja umsóknarform',
    updateApplicationFormError: 'Ekki tókst að uppfæra umsóknarform',
    createJobError: 'Ekki tókst að búa til starf',
    updateJobError: 'Ekki tókst að uppfæra starf',
    accessUpdateError: 'Ekki tókst að uppfæra aðgang',
    deleteLanguageError: 'Ekki tókst að eyða tungumáli',
    exportError: 'Ekki tókst að hlaða niður umsækjendum',
    getJobError: 'Ekki tókst að sækja starf',
  },
};
